import React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { RadioGroup, RadioOption } from "@/components/Core/RadioGroup";
import { DeliveryManagedBy, DeliveryMethod } from "@/models/Customer";
import { InvoiceExportType } from "@/models/Organisation";
import dayjs from "dayjs";
import { isEInvoice } from "./deliveryMethod.utils";

interface Props {
	value: DeliveryMethod;
	onChange: (value: DeliveryMethod) => void;
	/** Methods to offer, see `getDeliveryMethodOptions`. */
	options: DeliveryMethod[];
	exportType?: InvoiceExportType | string;
	/** Set when the integration owns the method: the group is locked. */
	managedBy?: DeliveryManagedBy;
	observedAt?: string;
	disabled?: boolean;
	/** Muted line under the group, e.g. "hämtas från koncernen". */
	note?: string;
	errors?: string;
	containerClassName?: string;
}

/**
 * Leveranssätt picker for a customer or corporate group. Under Fortnox the
 * e-invoice options can be picked as an intent: each carries "not confirmed by
 * Fortnox" until Fortnox manages the customer, and the managed value then
 * shows when Fortnox was last read instead.
 */
const DeliveryMethodRadioGroup: React.FC<Props> = ({
	value,
	onChange,
	options,
	exportType,
	managedBy,
	observedAt,
	disabled,
	note,
	errors,
	containerClassName,
}) => {
	const { t } = useTranslation();

	const managedHint = managedBy
		? t("deliveryMethod.managedByFortnox", {
				date: observedAt ? dayjs(observedAt).format("YYYY-MM-DD") : "–",
		  })
		: undefined;

	function hintFor(method: DeliveryMethod): string | undefined {
		if (exportType === "FORTNOX" && isEInvoice(method)) {
			return method === value && managedHint
				? managedHint
				: t("deliveryMethod.notConfirmedByFortnox");
		}

		return undefined;
	}

	const radioOptions: RadioOption[] = options.map((method) => ({
		value: method,
		label: t(`deliveryMethod.label.${method}` as any),
		hint: hintFor(method),
	}));

	return (
		<div className={cn("flex flex-col gap-1", containerClassName)}>
			<RadioGroup
				name="deliveryMethod"
				label={t("deliveryMethod.title")}
				knowledgeBaseTag="invoice-delivery-method"
				skipTranslation
				options={radioOptions}
				value={value}
				onChange={(next) => onChange(next as DeliveryMethod)}
				disabled={disabled || !!managedBy}
				wrapperClassName="grid grid-cols-2 gap-x-3 gap-y-2.5"
				errors={errors}
			/>
			{/* Under Fortnox the managed hint sits on the e-invoice option itself */}
			{managedHint && !(exportType === "FORTNOX" && isEInvoice(value)) && (
				<p className="text-xs text-gray-500">{managedHint}</p>
			)}
			{note && <p className="text-xs text-gray-500">{note}</p>}
		</div>
	);
};

export default DeliveryMethodRadioGroup;
